"use client";

import { useState } from "react";
import {
  ErrorNotice,
  Notice,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  CARD,
  WIDGET_H2,
} from "@/components/tool-kit/styles";
import {
  PairSummary,
  PrimerCard,
} from "@/components/tools/biology-genetics/primer-parts";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { parseNumberInput } from "@/lib/parse-number";
import {
  type Conditions,
  monovalentEquivalent,
  normalizePrimer,
  threePrimeMatch,
} from "@/lib/tool-kit/biology-genetics/primer-tm";

/** English strings. A translated content file overrides these through `ui`. */
export const PRIMER_TM_CALCULATOR_STRINGS = {
  firstLabel: "Primer 1 (forward) — 5′ to 3′",
  secondLabel: "Primer 2 (reverse), optional — 5′ to 3′",
  primerHint: "Letters A, C, G, T (U is read as T). Spaces are ignored.",
  tryExample: "Try an example",
  conditionsHeading: "Reaction conditions",
  oligoLabel: "Oligo concentration (nM)",
  naLabel: "Na⁺ (mM)",
  kLabel: "K⁺ (mM)",
  trisLabel: "Tris (mM)",
  mgLabel: "Mg²⁺ (mM)",
  dntpLabel: "dNTPs, total (mM)",
  presetPcr: "Typical PCR buffer",
  presetBasic: "50 mM Na⁺ only",
  equivalent: "Sodium equivalent used for the salt correction: {value} mM",
  results: "Results",
  primerHeading: "Primer {n}",
  methodNn: "Nearest-neighbour (SantaLucia 1998)",
  methodSalt: "Salt-adjusted GC formula",
  methodWallace: "Wallace rule",
  methodColumn: "Method",
  tmColumn: "Tm",
  noteColumn: "Formula",
  noteNn: "ΔH / (ΔS + R ln(C/4)), ΔS corrected for salt",
  noteSalt: "81.5 + 16.6 log₁₀[Na⁺] + 0.41 (%GC) − 675 / N",
  noteWallace: "2 (A + T) + 4 (G + C)",
  unitC: "°C",
  unitNt: "nt",
  length: "Length",
  gcContent: "GC content",
  wallaceWarning:
    "The Wallace rule was fitted to oligos of up to about {limit} bases and runs high for longer ones; use the nearest-neighbour value for PCR primers.",
  thermo:
    "ΔH = {dh} kcal/mol, ΔS = {ds} cal/(K·mol), ΔG at 37 °C = {dg} kcal/mol.",
  selfComplementary:
    "The primer is self-complementary, so the symmetric duplex model is used.",
  pass: "Meets the guideline",
  warn: "Outside the guideline",
  checkLength: "Length of 18–30 bases",
  checkGc: "GC content of 40–60%",
  checkClamp: "No more than 3 G or C among the last 5 bases at the 3′ end",
  checkRuns: "No run of more than 4 identical bases (longest run: {run})",
  pairHeading: "Primer pair",
  deltaTm: "Difference in Tm",
  annealing: "Suggested annealing temperature",
  annealingNote:
    "Annealing temperature is taken as 5 °C below the lower Tm, a common starting point to optimise with a gradient.",
  deltaOk: "The two Tm values are within 2 °C of each other.",
  deltaWide:
    "The Tm values differ by more than 2 °C; redesign one primer so both anneal at the same temperature.",
  noDimer:
    "No 3′-end complementarity found between the primers or within either one.",
  dimerSelfFirst:
    "The 3′ end of primer 1 pairs with another part of primer 1 — a possible hairpin or self-dimer.",
  dimerSelfSecond:
    "The 3′ end of primer 2 pairs with another part of primer 2 — a possible hairpin or self-dimer.",
  dimerCross:
    "The 3′ end of one primer pairs with the other primer — a possible primer-dimer.",
  prompt: "Enter a primer sequence to calculate its melting temperature.",
  errorBases: "Primer 1 contains letters other than A, C, G and T.",
  errorBasesSecond: "Primer 2 contains letters other than A, C, G and T.",
  errorShort: "Enter at least 2 bases.",
  errorNumbers: "Enter each concentration as a number of 0 or more.",
  errorSalt:
    "Add some Na⁺, K⁺, Tris or Mg²⁺; a salt correction needs a non-zero ion concentration.",
  errorOligo: "The oligo concentration must be above zero.",
} as const;

const EXAMPLE_FIRST = "AGCCGCAGCCTTTGTGAACC";
const EXAMPLE_SECOND = "TTCCACAATGCCACGCTTCTGC";

interface Fields {
  oligo: string;
  na: string;
  k: string;
  tris: string;
  mg: string;
  dntp: string;
}

const BASIC: Fields = {
  oligo: "250",
  na: "50",
  k: "0",
  tris: "0",
  mg: "0",
  dntp: "0",
};
const PCR: Fields = {
  oligo: "500",
  na: "0",
  k: "50",
  tris: "10",
  mg: "1.5",
  dntp: "0.8",
};

function readConditions(fields: Fields): Conditions | null {
  const values = [
    fields.oligo,
    fields.na,
    fields.k,
    fields.tris,
    fields.mg,
    fields.dntp,
  ].map(parseNumberInput);
  if (values.some((value) => value === null || value < 0)) {
    return null;
  }
  const [oligoNm, naMm, kMm, trisMm, mgMm, dntpMm] = values as number[];
  return { oligoNm, naMm, kMm, trisMm, mgMm, dntpMm };
}

function findProblem(
  strings: typeof PRIMER_TM_CALCULATOR_STRINGS,
  input: {
    conditions: Conditions | null;
    first: string;
    second: string;
    a: string | null;
    b: string | null;
  }
): string | null {
  const { conditions, a, b } = input;
  if (conditions === null) {
    return strings.errorNumbers;
  }
  if (monovalentEquivalent(conditions) <= 0) {
    return strings.errorSalt;
  }
  if (conditions.oligoNm <= 0) {
    return strings.errorOligo;
  }
  if (input.first.trim() !== "" && a === null) {
    return strings.errorBases;
  }
  if (input.second.trim() !== "" && b === null) {
    return strings.errorBasesSecond;
  }
  return (a?.length ?? 2) < 2 || (b?.length ?? 2) < 2
    ? strings.errorShort
    : null;
}

export default function PrimerTmCalculatorWidget() {
  const strings = useToolUi(PRIMER_TM_CALCULATOR_STRINGS);
  const fmt = useToolNumber();
  const [first, setFirst] = useState(EXAMPLE_FIRST);
  const [second, setSecond] = useState(EXAMPLE_SECOND);
  const [fields, setFields] = useState<Fields>(BASIC);

  const conditions = readConditions(fields);
  const a = normalizePrimer(first);
  const b = second.trim() === "" ? null : normalizePrimer(second);
  const firstBlank = first.trim() === "";

  function setField(name: keyof Fields, value: string) {
    setFields((previous) => ({ ...previous, [name]: value }));
  }

  const fieldList: { name: keyof Fields; label: string }[] = [
    { name: "oligo", label: strings.oligoLabel },
    { name: "na", label: strings.naLabel },
    { name: "k", label: strings.kLabel },
    { name: "tris", label: strings.trisLabel },
    { name: "mg", label: strings.mgLabel },
    { name: "dntp", label: strings.dntpLabel },
  ];

  const problem = findProblem(strings, {
    conditions,
    first,
    second,
    a,
    b,
  });
  const ready = problem === null && conditions !== null && a !== null;
  const matches =
    a !== null && b !== null
      ? {
          selfFirst: threePrimeMatch(a, a),
          selfSecond: threePrimeMatch(b, b),
          cross: threePrimeMatch(a, b) || threePrimeMatch(b, a),
        }
      : null;

  return (
    <div className="space-y-6">
      <div className="grid gap-4">
        <TextField
          hint={strings.primerHint}
          id="ptm-first"
          label={strings.firstLabel}
          mono
          onChange={setFirst}
          value={first}
        />
        <TextField
          id="ptm-second"
          label={strings.secondLabel}
          mono
          onChange={setSecond}
          value={second}
        />
      </div>
      <button
        className={BUTTON_SECONDARY}
        onClick={() => {
          setFirst(EXAMPLE_FIRST);
          setSecond(EXAMPLE_SECOND);
        }}
        type="button"
      >
        {strings.tryExample}
      </button>
      <div className={`${CARD} space-y-4`}>
        <h2 className="font-medium text-foreground text-sm">
          {strings.conditionsHeading}
        </h2>
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
          {fieldList.map((field) => (
            <TextField
              id={`ptm-${field.name}`}
              key={field.name}
              label={field.label}
              mono
              numeric
              onChange={(value) => setField(field.name, value)}
              value={fields[field.name]}
            />
          ))}
        </div>
        <div className="flex flex-wrap gap-3">
          <button
            className={BUTTON_SECONDARY}
            onClick={() => setFields(PCR)}
            type="button"
          >
            {strings.presetPcr}
          </button>
          <button
            className={BUTTON_SECONDARY}
            onClick={() => setFields(BASIC)}
            type="button"
          >
            {strings.presetBasic}
          </button>
        </div>
        {conditions !== null && monovalentEquivalent(conditions) > 0 ? (
          <p className="text-muted-foreground text-xs">
            {t(strings.equivalent, {
              value: fmt(monovalentEquivalent(conditions), {
                maximumFractionDigits: 1,
              }),
            })}
          </p>
        ) : null}
      </div>
      {problem ? <ErrorNotice>{problem}</ErrorNotice> : null}
      {firstBlank && problem === null ? (
        <Notice>{strings.prompt}</Notice>
      ) : null}
      {ready && conditions ? (
        <div aria-live="polite" className="space-y-4">
          <h2 className={WIDGET_H2}>{strings.results}</h2>
          <PrimerCard
            conditions={conditions}
            fmt={fmt}
            heading={t(strings.primerHeading, { n: fmt(1) })}
            sequence={a}
            strings={strings}
          />
          {b !== null ? (
            <PrimerCard
              conditions={conditions}
              fmt={fmt}
              heading={t(strings.primerHeading, { n: fmt(2) })}
              sequence={b}
              strings={strings}
            />
          ) : null}
          {a !== null && b !== null && matches ? (
            <PairSummary
              conditions={conditions}
              first={a}
              fmt={fmt}
              matches={matches}
              second={b}
              strings={strings}
            />
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
